import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Eye, Download, Trash2, Pencil, Clock, Library, Loader2, AlertCircle, CheckCircle2, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import type { Template } from "@/types/template";
import { useState } from "react";
import { ProcessedTemplateDialog } from "../ProcessedTemplateDialog";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/hooks/use-toast";
import { format } from "date-fns";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

interface TemplateTableProps {
  templates: Template[];
  onDelete: (template: Template) => void;
  onDownload: (template: Template) => void;
  isAdmin?: boolean;
}

export function TemplateTable({ templates, onDelete, onDownload, isAdmin = false }: TemplateTableProps) {
  const [showProcessedTemplate, setShowProcessedTemplate] = useState<Template | null>(null);
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const toggleCompanyLibrary = async (template: Template) => {
    try {
      const { error } = await supabase
        .from("templates")
        .update({
          is_company_library: !template.is_company_library
        })
        .eq("id", template.id);

      if (error) {
        throw error;
      }

      template.is_company_library = !template.is_company_library;
      
      toast({
        title: "Success",
        description: template.is_company_library 
          ? "Template added to company library" 
          : "Template removed from company library",
      });
    } catch (error) {
      console.error("Error toggling company library status:", error);
      toast({
        title: "Error",
        description: "Failed to update template status",
        variant: "destructive",
      });
    }
  };

  const getApprovalStatusBadge = (template: Template) => {
    const adminApprovalStatus = (template as any).admin_approval_status;
    const processingStatus = (template as any).processing_status;
    
    // Show processing status first if still processing
    if (processingStatus === 'pending' || processingStatus === 'processing') {
      return (
        <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200">
          <Clock className="h-3 w-3 mr-1" />
          Processing
        </Badge>
      );
    }
    
    if (processingStatus === 'failed') {
      return (
        <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">
          <AlertCircle className="h-3 w-3 mr-1" />
          Processing Failed
        </Badge>
      );
    }

    // Show admin approval status for completed processing
    switch (adminApprovalStatus) {
      case 'pending':
        return (
          <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200">
            <Clock className="h-3 w-3 mr-1" />
            Awaiting Admin Review
          </Badge>
        );
      case 'approved':
        if (template.is_company_library) {
          return (
            <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
              <Library className="h-3 w-3 mr-1" />
              Company Library
            </Badge>
          );
        }
        return (
          <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
            <CheckCircle2 className="h-3 w-3 mr-1" />
            Approved
          </Badge>
        );
      case 'rejected':
        return (
          <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">
            <XCircle className="h-3 w-3 mr-1" />
            Rejected
          </Badge>
        );
      default:
        return (
          <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200">
            <Clock className="h-3 w-3 mr-1" />
            Awaiting Admin Review
          </Badge>
        );
    }
  };

  return (
    <TooltipProvider>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Template Name</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Added On</TableHead>
            {isAdmin && <TableHead>Company Library</TableHead>}
            <TableHead className="text-center" colSpan={4}>Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {templates.map((template) => {
            const adminApprovalStatus = (template as any).admin_approval_status;
            const processingStatus = (template as any).processing_status;
            const adminUploadedHtml = (template as any).admin_uploaded_html;
            
            const showDeleteButton = !template.is_company_library || (template.is_company_library && isAdmin);
            const showDownloadButton = adminApprovalStatus === 'approved' && (adminUploadedHtml || template.processed_html);
            const showEditButton = adminApprovalStatus === 'approved';
            const isPendingReview = adminApprovalStatus === 'pending' || !adminApprovalStatus;
            const isProcessing = processingStatus === 'processing' || processingStatus === 'pending';
            const isRejected = adminApprovalStatus === 'rejected';

            return (
              <TableRow key={template.id}>
                <TableCell className="font-medium">{template.filename}</TableCell>
                <TableCell>{getApprovalStatusBadge(template)}</TableCell>
                <TableCell>{format(new Date(template.created_at), "MMM d, yyyy h:mm a")}</TableCell>
                {isAdmin && (
                  <TableCell>
                    {template.is_company_library ? (
                      <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                        <Library className="h-3 w-3 mr-1" />
                        Company Library
                      </Badge>
                    ) : (
                      <Badge variant="outline">Personal</Badge>
                    )}
                  </TableCell>
                )}
                <TableCell className="text-center">
                  {showEditButton && !isProcessing && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => navigate(`/templates/${template.id}/edit`)}
                          className="h-8 w-8"
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Edit Template</p>
                      </TooltipContent>
                    </Tooltip>
                  )}
                </TableCell>
                <TableCell className="text-center">
                  {showDownloadButton && !isProcessing && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => onDownload(template)}
                          className="h-8 w-8"
                        >
                          <Download className="h-4 w-4" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Download Template</p>
                      </TooltipContent>
                    </Tooltip>
                  )}
                </TableCell>
                <TableCell className="text-center">
                  {isPendingReview && !isProcessing && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          disabled
                        >
                          <Clock className="h-4 w-4 text-muted-foreground" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Awaiting Admin Review</p>
                      </TooltipContent>
                    </Tooltip>
                  )}
                </TableCell>
                <TableCell className="text-center">
                  {isRejected && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          disabled
                        >
                          <XCircle className="h-4 w-4 text-destructive" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Template Rejected</p>
                      </TooltipContent>
                    </Tooltip>
                  )}
                </TableCell>
                <TableCell className="text-center">
                  {showDeleteButton && !isProcessing && (
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-destructive hover:text-destructive"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Delete Template</AlertDialogTitle>
                          <AlertDialogDescription>
                            Are you sure you want to delete this template? This action cannot be undone.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={() => onDelete(template)}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                          >
                            Delete
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>

      {showProcessedTemplate && (
        <ProcessedTemplateDialog
          template={showProcessedTemplate}
          open={!!showProcessedTemplate}
          onOpenChange={() => setShowProcessedTemplate(null)}
        />
      )}
    </TooltipProvider>
  );
}
